/**
 * BSGOTECH — Corporate landing page.
 *
 * Presents BSGOTECH as the parent technology infrastructure company.
 * NEUNEXUS is presented as the platform product; four divisions extend
 * from it: NeuGreen Energy (active), NEUNEXUS Factory (proposed —
 * Smart Factory Uetze), NEUNEXUS Health / Renalix (strategic vision),
 * NEUNEXUS Enterprise (roadmap).
 *
 * Language: DE / EN / PT via shared LangCtx.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ChevronRight, Cpu, Cloud, Boxes, Radar, Cog, ShieldCheck,
  Database, SunMedium, Battery, Factory, Bot, HeartPulse, Building2,
  Layers, Zap, Rocket, CheckCircle2, Sparkles,
} from "lucide-react";
import { LangCtx, LanguageSwitcher } from "@/i18n/neunexus";
import { SUPPLIERS } from "@/data/partners";
import TechQuiz from "@/pages/bsgotech/TechQuiz";
import EcosystemArchitecture from "@/pages/bsgotech/EcosystemArchitecture";
import BrandCards from "@/pages/bsgotech/BrandCards";
import FounderCard from "@/pages/bsgotech/FounderCard";
import InnovationPathways from "@/pages/bsgotech/InnovationPathways";
import GlobalSearch from "@/pages/bsgotech/GlobalSearch";

// -------------------------------------------------------- Palette

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  blue:    "#3B82F6",
  amber:   "#F59E0B",
  purple:  "#A855F7",
  cyan:    "#06B6D4",
  rose:    "#F43F5E",
};

// -------------------------------------------------------- i18n

const T = {
  en: {
    nav:      { neunexus: "NeuNexus Platform", partners: "Partners", ops: "Operations", report: "Report" },
    hero: {
      badge: "Technology Infrastructure · Germany",
      brandA: "BSGO",
      brandB: "TECH",
      tagline: "Engineering Intelligent Ecosystems.",
      subtitle: "AI + Energy + Industry + Health — one European technology company building the infrastructure that connects software, hardware, data and artificial intelligence into operational ecosystems.",
      ctaPrimary: "Enter NeuNexus Platform",
      ctaSecondary: "Explore Divisions",
      ctaEcosystem: "Our Ecosystem",
      manualLink: "Corporate Manual",
      manualNote: "12-volume strategic, technical and operational reference",
      leadershipLink: "Leadership",
    },
    about: {
      eyebrow: "About BSGOTECH",
      title: "One company. Seven engineering disciplines.",
      subtitle: "BSGOTECH develops the technology infrastructure that powers modern industrial and health ecosystems — from silicon to intelligence.",
      pillars: [
        { icon: Cpu,        title: "Artificial Intelligence", desc: "Industrial AI, machine learning and RAG systems grounded in real operational data." },
        { icon: Cloud,      title: "Cloud Infrastructure",    desc: "Scalable European-hosted architecture for regulated industries." },
        { icon: Boxes,      title: "Industrial IoT",          desc: "Connected sensors, gateways and telemetry across production and energy assets." },
        { icon: Radar,      title: "Edge Computing",          desc: "Ruggedised compute close to the process — real-time inference where it matters." },
        { icon: Cog,        title: "Automation",              desc: "Process control, digital twins and orchestration for autonomous operations." },
        { icon: ShieldCheck,title: "Cybersecurity",           desc: "European security standards embedded across data, network and identity layers." },
        { icon: Database,   title: "Data Platforms",          desc: "Unified data planes that turn heterogeneous operational data into decisions." },
      ],
    },
    arch: {
      eyebrow: "Ecosystem Architecture",
      title: "How BSGOTECH is organised.",
      subtitle: "A single technology parent, a unified intelligence platform, four operating divisions — each with its own market, mission and roadmap.",
      parentLabel: "Parent · Technology Infrastructure",
      platformLabel: "Platform · AI & Digital Intelligence",
      divisionsLabel: "Divisions · Vertical Ecosystems",
    },
    divisions: {
      eyebrow: "Divisions",
      title: "Four ecosystems. One operational language.",
      items: [
        {
          id: "neugreen", color: "green",
          brand: "NeuGreen Energy",
          role: "Intelligent Energy Infrastructure",
          desc: "Renewable generation, storage and mobility — the first live operating division of the BSGOTECH ecosystem, delivered through NeuNexus.",
          status: "Active",
          bullets: ["Solar energy systems", "Battery Energy Storage (BESS)", "Hybrid inverters", "Energy Management (EMS)", "Industrial edge monitoring"],
          model: "CKD Manufacturing · SKD Assembly · OEM / Private Label",
          cta: "Enter NeuGreen",
          href: "/neugreen",
        },
        {
          id: "factory", color: "amber",
          brand: "NEUNEXUS Factory",
          role: "Smart Factory · Uetze Blueprint",
          desc: "The converged industrial demonstrator in Lower Saxony — Digital Twin, Industrial AI, cognitive robotics and closed-loop machine vision.",
          status: "Proposed collaboration",
          bullets: ["Digital Twin technology", "Robotics integration", "Industrial automation", "Predictive maintenance", "Closed-loop machine vision"],
          model: "Executive Strategic Report available",
          cta: "Read the Blueprint",
          href: "/report",
        },
        {
          id: "health", color: "rose",
          brand: "NEUNEXUS Health",
          role: "Renalix · AI Healthcare Platform",
          desc: "Predictive kidney intelligence for clinicians and patients — a healthcare data platform designed for European medical infrastructure.",
          status: "Launching 2027",
          bullets: ["Predictive Kidney Intelligence", "Renalix Care · patient app", "Renalix Pro · clinical dashboard", "Healthcare data analytics", "European clinical compliance"],
          model: "Strategic vision — clinical partnerships in scoping",
          cta: "Explore Renalix",
          href: "/renalix",
        },
        {
          id: "enterprise", color: "cyan",
          brand: "NEUNEXUS Enterprise",
          role: "Industrial Intelligence for Enterprises",
          desc: "The NeuNexus platform packaged for large European industrial customers — Digital Twin, RAG assistants and operational intelligence as a service.",
          status: "Roadmap 2028+",
          bullets: ["Enterprise Digital Twin", "RAG-based industrial assistants", "APIs & data connectors", "European hosting & compliance", "Managed operational intelligence"],
          model: "Platform-as-a-Service licensing",
          cta: "Preview roadmap",
          href: "#roadmap",
        },
      ],
    },
    platform: {
      eyebrow: "NEUNEXUS Platform",
      title: "From hardware to decisions.",
      subtitle: "A single technology plane connects physical assets to executive decisions — every layer instrumented, every decision auditable.",
      stack: [
        { icon: Boxes,   title: "Hardware",       desc: "Sensors, controllers, storage and generation assets across the factory floor." },
        { icon: Radar,   title: "Edge Computing", desc: "Ruggedised industrial compute with real-time inference at the process." },
        { icon: Database,title: "Data Platform",  desc: "Unified data plane with lineage, quality and governance." },
        { icon: Cpu,     title: "AI Engine",      desc: "Digital Twin, machine learning, RAG assistants and predictive services." },
        { icon: Sparkles,title: "Smart Decisions",desc: "Operational, tactical and strategic decisions grounded in evidence." },
      ],
      capsTitle: "Cross-cutting capabilities",
      caps: ["Digital Twins", "Machine Learning", "APIs", "Industrial Intelligence"],
    },
    partners: {
      eyebrow: "Technology Ecosystem Partners",
      title: "European infrastructure, global technology partners.",
      subtitle: "Active and proposed engagements across energy, industrial automation, robotics and hardware infrastructure.",
      groups: [
        { label: "Energy Technology",       ids: ["bluesun", "wattzing", "beny"] },
        { label: "Industrial Automation",   ids: ["siemens", "vcow"] },
        { label: "Precision Vision & Robotics", ids: ["dfoptix", "neura"] },
        { label: "Applied Research",        ids: ["fraunhofer"] },
      ],
      proposed: "Proposed collaboration",
      cta: "See partner directory",
    },
    roadmap: {
      eyebrow: "Growth Roadmap",
      title: "From energy platform to a global intelligent ecosystem.",
      subtitle: "A five-horizon roadmap — each horizon a concrete engineering milestone, not a marketing headline.",
      years: [
        { year: "2026", title: "NEUNEXUS Foundation & Energy Platform", desc: "Consolidate the NeuNexus platform, launch NeuGreen Energy operations, establish the Uetze blueprint.", status: "Active",  color: "green" },
        { year: "2027", title: "Renalix Healthcare Expansion",           desc: "Launch NEUNEXUS Health (Renalix) — predictive kidney intelligence for European clinical partners.", status: "Next",    color: "amber" },
        { year: "2028", title: "Smart Factory & Local Assembly",         desc: "Commission the Smart Factory Uetze demonstrator and initiate local CKD/SKD assembly.",             status: "Planned", color: "blue"  },
        { year: "2029", title: "European Market Expansion",              desc: "Replicate the reference stack across additional European industrial sites.",                       status: "Planned", color: "purple"},
        { year: "2030+",title: "Global Intelligent Ecosystem",           desc: "Extend the platform to adjacent verticals and international markets.",                             status: "Vision",  color: "cyan"  },
      ],
    },
    footer: {
      line1: "BSGOTECH · Engineering Intelligent Ecosystems",
      line2: "© 2026 · BSGOTECH · Uetze · Lower Saxony · Germany",
    },
  },
  de: {
    nav:      { neunexus: "NeuNexus-Plattform", partners: "Partner", ops: "Operations", report: "Bericht" },
    hero: {
      badge: "Technologie-Infrastruktur · Deutschland",
      brandA: "BSGO",
      brandB: "TECH",
      tagline: "Intelligente Ökosysteme entwickeln.",
      subtitle: "KI + Energie + Industrie + Gesundheit — ein europäisches Technologieunternehmen, das die Infrastruktur baut, die Software, Hardware, Daten und Künstliche Intelligenz zu operativen Ökosystemen verbindet.",
      ctaPrimary: "NeuNexus-Plattform öffnen",
      ctaSecondary: "Divisionen ansehen",
      ctaEcosystem: "Unser Ökosystem",
      manualLink: "Corporate Manual",
      manualNote: "12-Bände-Referenz für Strategie, Technik und Betrieb",
      leadershipLink: "Leadership",
    },
    about: {
      eyebrow: "Über BSGOTECH",
      title: "Ein Unternehmen. Sieben Ingenieursdisziplinen.",
      subtitle: "BSGOTECH entwickelt die Technologie-Infrastruktur moderner industrieller und medizinischer Ökosysteme — vom Silizium bis zur Intelligenz.",
      pillars: [
        { icon: Cpu,        title: "Künstliche Intelligenz",  desc: "Industrielle KI, Machine Learning und RAG-Systeme, verankert in realen Betriebsdaten." },
        { icon: Cloud,      title: "Cloud-Infrastruktur",     desc: "Skalierbare, europäisch gehostete Architektur für regulierte Branchen." },
        { icon: Boxes,      title: "Industrielles IoT",       desc: "Vernetzte Sensoren, Gateways und Telemetrie über Produktions- und Energieanlagen." },
        { icon: Radar,      title: "Edge Computing",          desc: "Ruggedisierte Rechenleistung am Prozess — Echtzeit-Inferenz dort, wo es zählt." },
        { icon: Cog,        title: "Automatisierung",         desc: "Prozesssteuerung, Digital Twins und Orchestrierung für autonome Betriebe." },
        { icon: ShieldCheck,title: "Cybersicherheit",         desc: "Europäische Sicherheitsstandards, eingebettet in Daten-, Netzwerk- und Identitätsschichten." },
        { icon: Database,   title: "Datenplattformen",        desc: "Einheitliche Datenebenen, die heterogene Betriebsdaten in Entscheidungen verwandeln." },
      ],
    },
    arch: {
      eyebrow: "Ökosystem-Architektur",
      title: "So ist BSGOTECH organisiert.",
      subtitle: "Ein Technologie-Konzern, eine einheitliche Intelligenzplattform, vier operative Divisionen — jede mit eigenem Markt, eigener Mission und eigenem Fahrplan.",
      parentLabel: "Konzern · Technologie-Infrastruktur",
      platformLabel: "Plattform · KI & Digitale Intelligenz",
      divisionsLabel: "Divisionen · Vertikale Ökosysteme",
    },
    divisions: {
      eyebrow: "Divisionen",
      title: "Vier Ökosysteme. Eine operative Sprache.",
      items: [
        {
          id: "neugreen", color: "green",
          brand: "NeuGreen Energy",
          role: "Intelligente Energie-Infrastruktur",
          desc: "Erneuerbare Erzeugung, Speicherung und Mobilität — die erste operative Division des BSGOTECH-Ökosystems, bereitgestellt über NeuNexus.",
          status: "Aktiv",
          bullets: ["Solar-Energiesysteme", "Batteriespeicher (BESS)", "Hybrid-Wechselrichter", "Energiemanagement (EMS)", "Industrielles Edge-Monitoring"],
          model: "CKD-Fertigung · SKD-Montage · OEM / Private Label",
          cta: "NeuGreen öffnen",
          href: "/neugreen",
        },
        {
          id: "factory", color: "amber",
          brand: "NEUNEXUS Factory",
          role: "Smart Factory · Uetze-Blueprint",
          desc: "Der konvergente Industrie-Demonstrator in Niedersachsen — Digital Twin, industrielle KI, kognitive Robotik und geschlossene Machine Vision.",
          status: "Vorgeschlagene Kooperation",
          bullets: ["Digital-Twin-Technologie", "Robotik-Integration", "Industrielle Automatisierung", "Vorausschauende Instandhaltung", "Geschlossene Machine Vision"],
          model: "Executive Strategiebericht verfügbar",
          cta: "Blueprint lesen",
          href: "/report",
        },
        {
          id: "health", color: "rose",
          brand: "NEUNEXUS Health",
          role: "Renalix · KI-Gesundheitsplattform",
          desc: "Prädiktive Niereninformationen für Kliniker und Patienten — eine Gesundheitsdatenplattform für die europäische medizinische Infrastruktur.",
          status: "Start 2027",
          bullets: ["Prädiktive Nieren-Intelligenz", "Renalix Care · Patienten-App", "Renalix Pro · klinisches Dashboard", "Gesundheitsdatenanalyse", "Europäische klinische Compliance"],
          model: "Strategische Vision — klinische Partnerschaften in Sondierung",
          cta: "Renalix entdecken",
          href: "/renalix",
        },
        {
          id: "enterprise", color: "cyan",
          brand: "NEUNEXUS Enterprise",
          role: "Industrielle Intelligenz für Unternehmen",
          desc: "Die NeuNexus-Plattform verpackt für europäische Industriekunden — Digital Twin, RAG-Assistenten und operative Intelligenz als Service.",
          status: "Fahrplan 2028+",
          bullets: ["Enterprise Digital Twin", "RAG-basierte Industrieassistenten", "APIs & Datenkonnektoren", "Europäisches Hosting & Compliance", "Managed Operational Intelligence"],
          model: "Platform-as-a-Service-Lizenzierung",
          cta: "Fahrplan ansehen",
          href: "#roadmap",
        },
      ],
    },
    platform: {
      eyebrow: "NEUNEXUS-Plattform",
      title: "Von der Hardware zur Entscheidung.",
      subtitle: "Eine einzige Technologie-Ebene verbindet physische Assets mit Führungsentscheidungen — jede Schicht instrumentiert, jede Entscheidung auditierbar.",
      stack: [
        { icon: Boxes,   title: "Hardware",         desc: "Sensoren, Steuerungen, Speicher und Erzeugungsanlagen auf dem Fabrikboden." },
        { icon: Radar,   title: "Edge Computing",   desc: "Ruggedisierte industrielle Rechenleistung mit Echtzeit-Inferenz am Prozess." },
        { icon: Database,title: "Datenplattform",   desc: "Einheitliche Datenebene mit Lineage, Qualität und Governance." },
        { icon: Cpu,     title: "KI-Engine",        desc: "Digital Twin, Machine Learning, RAG-Assistenten und prädiktive Dienste." },
        { icon: Sparkles,title: "Smarte Entscheidungen", desc: "Operative, taktische und strategische Entscheidungen auf Basis von Evidenz." },
      ],
      capsTitle: "Übergreifende Fähigkeiten",
      caps: ["Digital Twins", "Machine Learning", "APIs", "Industrielle Intelligenz"],
    },
    partners: {
      eyebrow: "Technologie-Ökosystempartner",
      title: "Europäische Infrastruktur, globale Technologiepartner.",
      subtitle: "Aktive und vorgeschlagene Kooperationen in Energie, industrieller Automatisierung, Robotik und Hardware-Infrastruktur.",
      groups: [
        { label: "Energietechnologie",         ids: ["bluesun", "wattzing", "beny"] },
        { label: "Industrielle Automatisierung", ids: ["siemens", "vcow"] },
        { label: "Präzisionsvision & Robotik",   ids: ["dfoptix", "neura"] },
        { label: "Angewandte Forschung",         ids: ["fraunhofer"] },
      ],
      proposed: "Vorgeschlagene Kooperation",
      cta: "Partnerverzeichnis öffnen",
    },
    roadmap: {
      eyebrow: "Wachstumsfahrplan",
      title: "Von der Energie-Plattform zum globalen intelligenten Ökosystem.",
      subtitle: "Ein Fahrplan mit fünf Horizonten — jeder Horizont ein konkreter Ingenieurs-Meilenstein, keine Marketing-Zeile.",
      years: [
        { year: "2026", title: "NEUNEXUS-Fundament & Energie-Plattform", desc: "Konsolidierung der NeuNexus-Plattform, Start des NeuGreen-Energiebetriebs, Aufbau des Uetze-Blueprints.", status: "Aktiv",     color: "green" },
        { year: "2027", title: "Renalix Gesundheits-Expansion",          desc: "Start von NEUNEXUS Health (Renalix) — prädiktive Nieren-Intelligenz für europäische Kliniken.",              status: "Nächste",   color: "amber" },
        { year: "2028", title: "Smart Factory & lokale Montage",         desc: "Inbetriebnahme des Smart-Factory-Uetze-Demonstrators und Aufbau lokaler CKD/SKD-Montage.",                   status: "Geplant",   color: "blue"  },
        { year: "2029", title: "Europäische Marktexpansion",             desc: "Replikation des Referenz-Stacks an weiteren europäischen Standorten.",                                       status: "Geplant",   color: "purple"},
        { year: "2030+",title: "Globales intelligentes Ökosystem",       desc: "Erweiterung der Plattform in benachbarte Branchen und internationale Märkte.",                                status: "Vision",    color: "cyan"  },
      ],
    },
    footer: {
      line1: "BSGOTECH · Intelligente Ökosysteme entwickeln",
      line2: "© 2026 · BSGOTECH · Uetze · Niedersachsen · Deutschland",
    },
  },
  pt: {
    nav:      { neunexus: "Plataforma NeuNexus", partners: "Parceiros", ops: "Operações", report: "Relatório" },
    hero: {
      badge: "Infraestrutura Tecnológica · Alemanha",
      brandA: "BSGO",
      brandB: "TECH",
      tagline: "A construir ecossistemas inteligentes.",
      subtitle: "IA + Energia + Indústria + Saúde — uma empresa tecnológica europeia que constrói a infraestrutura que liga software, hardware, dados e inteligência artificial em ecossistemas operacionais.",
      ctaPrimary: "Entrar na Plataforma NeuNexus",
      ctaSecondary: "Ver Divisões",
      ctaEcosystem: "O nosso ecossistema",
      manualLink: "Manual Corporativo",
      manualNote: "Referência estratégica, técnica e operacional em 12 volumes",
    },
    about: {
      eyebrow: "Sobre a BSGOTECH",
      title: "Uma empresa. Sete disciplinas de engenharia.",
      subtitle: "A BSGOTECH desenvolve a infraestrutura tecnológica que alimenta os ecossistemas industriais e de saúde modernos — do silício à inteligência.",
      pillars: [
        { icon: Cpu,        title: "Inteligência Artificial", desc: "IA industrial, machine learning e sistemas RAG ancorados em dados operacionais reais." },
        { icon: Cloud,      title: "Infraestrutura Cloud",    desc: "Arquitetura escalável alojada na Europa para indústrias reguladas." },
        { icon: Boxes,      title: "IoT Industrial",          desc: "Sensores, gateways e telemetria conectados em ativos de produção e energia." },
        { icon: Radar,      title: "Edge Computing",          desc: "Computação ruggedizada junto ao processo — inferência em tempo real onde importa." },
        { icon: Cog,        title: "Automação",               desc: "Controlo de processo, gémeos digitais e orquestração para operações autónomas." },
        { icon: ShieldCheck,title: "Cibersegurança",          desc: "Standards de segurança europeus embutidos nas camadas de dados, rede e identidade." },
        { icon: Database,   title: "Plataformas de Dados",    desc: "Planos de dados unificados que transformam dados operacionais heterogéneos em decisões." },
      ],
    },
    arch: {
      eyebrow: "Arquitetura do Ecossistema",
      title: "Como a BSGOTECH está organizada.",
      subtitle: "Uma empresa-mãe tecnológica, uma plataforma unificada de inteligência, quatro divisões operacionais — cada uma com o seu mercado, missão e roadmap.",
      parentLabel: "Empresa-mãe · Infraestrutura Tecnológica",
      platformLabel: "Plataforma · IA & Inteligência Digital",
      divisionsLabel: "Divisões · Ecossistemas Verticais",
    },
    divisions: {
      eyebrow: "Divisões",
      title: "Quatro ecossistemas. Uma linguagem operacional.",
      items: [
        {
          id: "neugreen", color: "green",
          brand: "NeuGreen Energy",
          role: "Infraestrutura Energética Inteligente",
          desc: "Geração renovável, armazenamento e mobilidade — a primeira divisão operacional ativa do ecossistema BSGOTECH, entregue através da NeuNexus.",
          status: "Ativa",
          bullets: ["Sistemas de energia solar", "Armazenamento em bateria (BESS)", "Inversores híbridos", "Gestão de energia (EMS)", "Monitorização edge industrial"],
          model: "Fabricação CKD · Montagem SKD · OEM / Private Label",
          cta: "Entrar na NeuGreen",
          href: "/neugreen",
        },
        {
          id: "factory", color: "amber",
          brand: "NEUNEXUS Factory",
          role: "Smart Factory · Blueprint Uetze",
          desc: "O demonstrador industrial convergente na Baixa Saxónia — Gémeo Digital, IA Industrial, robótica cognitiva e machine vision em circuito fechado.",
          status: "Colaboração proposta",
          bullets: ["Tecnologia de Gémeo Digital", "Integração robótica", "Automação industrial", "Manutenção preditiva", "Machine vision em circuito fechado"],
          model: "Relatório Estratégico Executivo disponível",
          cta: "Ler o Blueprint",
          href: "/report",
        },
        {
          id: "health", color: "rose",
          brand: "NEUNEXUS Health",
          role: "Renalix · Plataforma de Saúde com IA",
          desc: "Inteligência preditiva renal para clínicos e pacientes — uma plataforma de dados de saúde para a infraestrutura médica europeia.",
          status: "Lançamento 2027",
          bullets: ["Inteligência renal preditiva", "Renalix Care · app de paciente", "Renalix Pro · painel clínico", "Análise de dados de saúde", "Compliance clínica europeia"],
          model: "Visão estratégica — parcerias clínicas em avaliação",
          cta: "Explorar Renalix",
          href: "/renalix",
        },
        {
          id: "enterprise", color: "cyan",
          brand: "NEUNEXUS Enterprise",
          role: "Inteligência Industrial para Empresas",
          desc: "A plataforma NeuNexus embalada para grandes clientes industriais europeus — Gémeo Digital, assistentes RAG e inteligência operacional como serviço.",
          status: "Roadmap 2028+",
          bullets: ["Gémeo Digital empresarial", "Assistentes industriais baseados em RAG", "APIs & conectores de dados", "Alojamento europeu & compliance", "Inteligência operacional gerida"],
          model: "Licenciamento Platform-as-a-Service",
          cta: "Ver roadmap",
          href: "#roadmap",
        },
      ],
    },
    platform: {
      eyebrow: "Plataforma NEUNEXUS",
      title: "Do hardware às decisões.",
      subtitle: "Um único plano tecnológico liga ativos físicos a decisões executivas — cada camada instrumentada, cada decisão auditável.",
      stack: [
        { icon: Boxes,   title: "Hardware",           desc: "Sensores, controladores, armazenamento e ativos de geração no chão de fábrica." },
        { icon: Radar,   title: "Edge Computing",     desc: "Computação industrial ruggedizada com inferência em tempo real junto ao processo." },
        { icon: Database,title: "Plataforma de Dados",desc: "Plano de dados unificado com linhagem, qualidade e governança." },
        { icon: Cpu,     title: "Motor de IA",        desc: "Gémeo Digital, machine learning, assistentes RAG e serviços preditivos." },
        { icon: Sparkles,title: "Decisões Inteligentes", desc: "Decisões operacionais, táticas e estratégicas ancoradas em evidência." },
      ],
      capsTitle: "Capacidades transversais",
      caps: ["Gémeos Digitais", "Machine Learning", "APIs", "Inteligência Industrial"],
    },
    partners: {
      eyebrow: "Parceiros do Ecossistema Tecnológico",
      title: "Infraestrutura europeia, parceiros tecnológicos globais.",
      subtitle: "Colaborações ativas e propostas em energia, automação industrial, robótica e infraestrutura de hardware.",
      groups: [
        { label: "Tecnologia Energética",       ids: ["bluesun", "wattzing", "beny"] },
        { label: "Automação Industrial",        ids: ["siemens", "vcow"] },
        { label: "Visão de Precisão & Robótica", ids: ["dfoptix", "neura"] },
        { label: "Investigação Aplicada",       ids: ["fraunhofer"] },
      ],
      proposed: "Colaboração proposta",
      cta: "Abrir diretório de parceiros",
    },
    roadmap: {
      eyebrow: "Roadmap de Crescimento",
      title: "De plataforma energética a ecossistema inteligente global.",
      subtitle: "Um roadmap de cinco horizontes — cada horizonte um marco concreto de engenharia, não uma frase de marketing.",
      years: [
        { year: "2026", title: "Fundação NEUNEXUS & Plataforma Energética", desc: "Consolidar a plataforma NeuNexus, lançar operações NeuGreen Energy, estabelecer o blueprint Uetze.", status: "Ativa",     color: "green" },
        { year: "2027", title: "Expansão Renalix (Saúde)",                   desc: "Lançar NEUNEXUS Health (Renalix) — inteligência renal preditiva para parceiros clínicos europeus.", status: "Próxima",   color: "amber" },
        { year: "2028", title: "Smart Factory & Montagem Local",             desc: "Comissionar o demonstrador Smart Factory Uetze e iniciar montagem local CKD/SKD.",               status: "Planeada",  color: "blue"  },
        { year: "2029", title: "Expansão no Mercado Europeu",                desc: "Replicar o stack de referência em locais industriais europeus adicionais.",                      status: "Planeada",  color: "purple"},
        { year: "2030+",title: "Ecossistema Inteligente Global",             desc: "Estender a plataforma a verticais adjacentes e mercados internacionais.",                        status: "Visão",     color: "cyan"  },
      ],
    },
    footer: {
      line1: "BSGOTECH · A construir ecossistemas inteligentes",
      line2: "© 2026 · BSGOTECH · Uetze · Baixa Saxónia · Alemanha",
    },
  },
};

const useT = () => {
  const { lang } = React.useContext(LangCtx);
  return T[lang] || T.en;
};

const COLOR = { green: P.green, amber: P.amber, blue: P.blue, purple: P.purple, cyan: P.cyan, rose: P.rose, silver: P.silver };
const partnerBySlug = (id) => SUPPLIERS.find((s) => s.id === id);

// -------------------------------------------------------- Building blocks

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative border-t ${className}`} style={{ background: P.bg, borderColor: P.border }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">{children}</div>
  </section>
);

const Eyebrow = ({ children, color = P.green }) => (
  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color }}>{children}</div>
);

const H2 = ({ children }) => (
  <h2 className="mt-3 font-semibold tracking-tight text-[26px] lg:text-[36px] leading-[1.1]" style={{ color: "#fff", letterSpacing: "-0.015em" }}>{children}</h2>
);

const Sub = ({ children }) => (
  <p className="mt-4 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>{children}</p>
);

// -------------------------------------------------------- Hero

const Hero = () => {
  const t = useT();
  return (
    <section className="relative overflow-hidden" style={{ background: P.bg }}>
      {/* atmospheric background */}
      <div className="absolute inset-0" aria-hidden="true" style={{
        backgroundImage: `radial-gradient(circle at 20% 20%, ${P.blue}18 0%, transparent 45%), radial-gradient(circle at 82% 78%, ${P.green}18 0%, transparent 45%), radial-gradient(circle at 50% 50%, ${P.purple}0d 0%, transparent 60%)`,
      }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24 lg:pt-36 lg:pb-32">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ background: `${P.green}12`, color: P.green, border: `1px solid ${P.green}44` }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.green }} />
          {t.hero.badge}
        </div>

        <img
          src="/bsgotech/logo-official.png"
          alt="BSGOTECH — Engineering Intelligent Ecosystems"
          className="mt-8 w-full max-w-[680px] h-auto select-none"
          data-testid="bsgotech-hero-logo"
          draggable="false"
        />

        <div className="mt-6 max-w-3xl text-[20px] lg:text-[26px] font-light leading-snug" style={{ color: P.ink }}>
          {t.hero.tagline}
        </div>
        <p className="mt-5 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
          {t.hero.subtitle}
        </p>

        {/* Vertical badges */}
        <div className="mt-7 flex flex-wrap gap-2">
          {[
            { l: "AI",       c: P.blue },
            { l: "Energy",   c: P.green },
            { l: "Industry", c: P.amber },
            { l: "Health",   c: P.rose },
          ].map((v, i) => (
            <span key={i} className="text-[10.5px] uppercase tracking-[0.24em] font-semibold px-2.5 py-1 rounded"
              style={{ background: `${v.c}12`, color: v.c, border: `1px solid ${v.c}44` }} data-testid={`hero-vertical-${v.l.toLowerCase()}`}>
              {v.l}
            </span>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/neunexus" data-testid="hero-cta-primary"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition"
            style={{ background: "#fff", color: "#0A0F1A" }}>
            {t.hero.ctaPrimary} <ArrowUpRight size={13} />
          </Link>
          <a href="#divisions" data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition hover:bg-white/5"
            style={{ border: `1px solid ${P.border}`, color: P.ink }}>
            {t.hero.ctaSecondary} <ChevronRight size={13} />
          </a>
          <Link to="/our-ecosystem" data-testid="hero-cta-ecosystem"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition hover:bg-white/5"
            style={{ border: `1px solid ${P.green}55`, color: P.green }}>
            {t.hero.ctaEcosystem} <ArrowUpRight size={13} />
          </Link>
        </div>

        {/* Strategic corporate manual link — discreet but always visible in the hero */}
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <Link to="/manual" data-testid="hero-manual-link"
            className="inline-flex items-center gap-3 group"
            style={{ color: P.silver }}>
            <span className="w-8 h-px transition group-hover:w-12" style={{ background: P.silver }} aria-hidden="true" />
            <span className="text-[10.5px] uppercase tracking-[0.32em] font-semibold transition group-hover:text-white">
              {t.hero.manualLink}
            </span>
            <span className="text-[11.5px]" style={{ color: P.dim }}>· {t.hero.manualNote}</span>
            <ArrowUpRight size={12} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link to="/leadership" data-testid="hero-leadership-link"
            className="inline-flex items-center gap-3 group"
            style={{ color: P.silver }}>
            <span className="w-8 h-px transition group-hover:w-12" style={{ background: P.silver }} aria-hidden="true" />
            <span className="text-[10.5px] uppercase tracking-[0.32em] font-semibold transition group-hover:text-white">
              {t.hero.leadershipLink}
            </span>
            <ArrowUpRight size={12} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------- About

const About = () => {
  const t = useT();
  return (
    <Section id="about">
      <Eyebrow>{t.about.eyebrow}</Eyebrow>
      <H2>{t.about.title}</H2>
      <Sub>{t.about.subtitle}</Sub>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {t.about.pillars.map((p, i) => {
          const Ic = p.icon;
          return (
            <div key={i} data-testid={`pillar-${i}`}
              className="rounded-lg p-5 transition hover:border-white/20"
              style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <Ic size={20} style={{ color: P.silver }} strokeWidth={1.6} />
              <div className="mt-3 text-[14px] font-semibold" style={{ color: "#fff" }}>{p.title}</div>
              <div className="mt-1.5 text-[11.5px] leading-relaxed" style={{ color: P.dim }}>{p.desc}</div>
            </div>
          );
        })}
      </div>

      {/* Founder profile — supportive, not the main focus of the page */}
      <div className="mt-14">
        <FounderCard variant="compact" />
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Ecosystem Architecture

const Arch = () => {
  const t = useT();
  return (
    <Section id="architecture">
      <div className="text-center">
        <Eyebrow color={P.blue}>{t.arch.eyebrow}</Eyebrow>
        <div className="max-w-3xl mx-auto">
          <H2>{t.arch.title}</H2>
        </div>
        <p className="mt-4 max-w-2xl mx-auto text-[13.5px] leading-relaxed" style={{ color: P.dim }}>{t.arch.subtitle}</p>
      </div>

      <div className="mt-14 relative">
        {/* Level 1 — BSGOTECH */}
        <div className="flex flex-col items-center">
          <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: P.silver }}>{t.arch.parentLabel}</div>
          <div
            className="mt-2 px-6 py-3.5 rounded-lg flex items-center gap-3"
            style={{
              background: `linear-gradient(135deg, ${P.green}18 0%, ${P.bg2} 100%)`,
              border: `1.5px solid ${P.green}66`,
              boxShadow: `0 0 32px ${P.green}22`,
            }}
            data-testid="arch-bsgotech"
          >
            <img src="/bsgotech/logo-official.png" alt="BSGOTECH" className="h-9 w-auto" />
          </div>
        </div>

        {/* connector */}
        <div className="mx-auto my-6 w-px h-10" style={{ background: `linear-gradient(180deg, ${P.green}66, ${P.blue}66)` }} />

        {/* Level 2 — NEUNEXUS */}
        <div className="flex flex-col items-center">
          <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: P.silver }}>{t.arch.platformLabel}</div>
          <Link
            to="/neunexus"
            data-testid="arch-neunexus"
            className="mt-2 px-6 py-3.5 rounded-lg flex items-center gap-3 transition hover:border-white/40"
            style={{
              background: `linear-gradient(135deg, ${P.blue}12 0%, ${P.bg2} 100%)`,
              border: `1.5px solid ${P.blue}66`,
            }}
          >
            <div className="text-[20px] font-semibold tracking-tight" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
              NEU<span style={{ color: P.blue }}>NEXUS</span>
            </div>
            <ArrowUpRight size={14} style={{ color: P.blue }} />
          </Link>
        </div>

        {/* connector */}
        <div className="mx-auto my-6 w-px h-10" style={{ background: `linear-gradient(180deg, ${P.blue}66, ${P.silver}44)` }} />

        {/* Level 3 — Divisions grid */}
        <div>
          <div className="text-center text-[10px] uppercase tracking-[0.32em]" style={{ color: P.silver }}>{t.arch.divisionsLabel}</div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {t.divisions.items.map((d) => {
              const c = COLOR[d.color] || P.silver;
              return (
                <a key={d.id} href={`#division-${d.id}`}
                  data-testid={`arch-div-${d.id}`}
                  className="rounded-lg p-4 transition hover:-translate-y-0.5"
                  style={{ background: `${c}08`, border: `1px solid ${c}55` }}>
                  <div className="text-[9.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: c }}>{d.status}</div>
                  <div className="mt-1.5 text-[14.5px] font-semibold tracking-tight" style={{ color: "#fff" }}>{d.brand}</div>
                  <div className="mt-1 text-[11px]" style={{ color: P.dim }}>{d.role}</div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Divisions

const DIV_ICONS = { neugreen: SunMedium, factory: Factory, health: HeartPulse, enterprise: Building2 };
const DIV_LOGO  = { health: "/renalix/logo-official.png", neugreen: "/neugreen/logo-official.png" };
const DIV_GALLERY = {
  neugreen: [
    { src: "/neugreen/products/solar-kit-scene.png",     alt: "NeuGreen edge computer + solar panel + smart inverter + battery" },
    { src: "/neugreen/products/complete-solar-kit.png",  alt: "NeuGreen complete solar kit — packaging and modules" },
    { src: "/neugreen/products/solar-kit-bom.png",       alt: "NeuGreen solar kit bill of materials (exploded view)" },
    { src: "/neugreen/products/edge-computer-exploded.png", alt: "NeuGreen edge computer — internals exploded view" },
  ],
};

const DivisionCard = ({ d }) => {
  const c = COLOR[d.color] || P.silver;
  const Ic = DIV_ICONS[d.id] || Layers;
  const logo = DIV_LOGO[d.id];
  const isInternal = d.href.startsWith("/");
  const Cta = isInternal ? Link : "a";
  const ctaProps = isInternal ? { to: d.href } : { href: d.href };
  return (
    <div id={`division-${d.id}`} data-testid={`division-${d.id}`}
      className="scroll-mt-24 rounded-xl overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${c}0a 0%, ${P.bg2} 55%)`, border: `1px solid ${c}44` }}>
      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-0">
        <div className="p-8 lg:p-10">
          <div className="flex items-center gap-3">
            {logo ? (
              <div className="w-11 h-11 rounded-md flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.96)", border: `1px solid ${c}44` }}>
                <img src={logo} alt={`${d.brand} logo`} className="max-h-8 max-w-8 object-contain" />
              </div>
            ) : (
              <div className="w-11 h-11 rounded-md flex items-center justify-center"
                style={{ background: `${c}18`, border: `1px solid ${c}66` }}>
                <Ic size={20} style={{ color: c }} strokeWidth={1.75} />
              </div>
            )}
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: c }}>{d.status}</div>
              <div className="text-[19px] font-semibold tracking-tight" style={{ color: "#fff", letterSpacing: "-0.01em" }}>{d.brand}</div>
            </div>
          </div>
          <div className="mt-4 text-[11px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>{d.role}</div>
          <p className="mt-3 text-[13.5px] leading-relaxed max-w-lg" style={{ color: P.dim }}>{d.desc}</p>

          <ul className="mt-5 grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
            {d.bullets.map((b, i) => (
              <li key={i} className="text-[12px] leading-relaxed flex gap-2" style={{ color: P.ink }}>
                <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: c }} />
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-6 text-[11px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>{d.model}</div>

          <Cta {...ctaProps}
            data-testid={`division-${d.id}-cta`}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold rounded-md transition"
            style={{ background: `${c}18`, color: c, border: `1px solid ${c}55` }}>
            {d.cta} <ArrowUpRight size={12} />
          </Cta>
        </div>

        <div className="relative min-h-[220px] lg:min-h-[100%] flex items-center justify-center p-6"
          style={{ background: `radial-gradient(circle at 60% 50%, ${c}22 0%, transparent 65%), ${P.bg}`, borderLeft: `1px solid ${P.border}` }}
          aria-hidden="true">
          {DIV_GALLERY[d.id] ? (
            <div className="grid grid-cols-2 gap-2 w-full max-w-[420px]" data-testid={`division-${d.id}-gallery`}>
              {DIV_GALLERY[d.id].map((img, i) => (
                <div key={i} className="relative rounded-md overflow-hidden group"
                  style={{ background: "rgba(255,255,255,0.96)", border: `1px solid ${c}33` }}>
                  <img src={img.src} alt={img.alt}
                    className="w-full h-24 lg:h-28 object-cover transition group-hover:scale-105"
                    loading="lazy" />
                </div>
              ))}
            </div>
          ) : logo ? (
            <div className="rounded-xl px-6 py-5 flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.96)", boxShadow: `0 0 40px ${c}44` }}>
              <img src={logo} alt="" className="max-h-16 max-w-[220px] object-contain" />
            </div>
          ) : (
            <div className="w-28 h-28 lg:w-36 lg:h-36 rounded-full flex items-center justify-center"
              style={{ background: `${c}18`, border: `1.5px solid ${c}77`, boxShadow: `0 0 40px ${c}44` }}>
              <Ic size={54} style={{ color: c }} strokeWidth={1.5} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Divisions = () => {
  const t = useT();
  return (
    <Section id="divisions">
      <Eyebrow color={P.amber}>{t.divisions.eyebrow}</Eyebrow>
      <H2>{t.divisions.title}</H2>
      <div className="mt-10 space-y-4">
        {t.divisions.items.map((d) => (<DivisionCard key={d.id} d={d} />))}
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Platform stack

const Platform = () => {
  const t = useT();
  return (
    <Section id="platform">
      <Eyebrow color={P.blue}>{t.platform.eyebrow}</Eyebrow>
      <H2>{t.platform.title}</H2>
      <Sub>{t.platform.subtitle}</Sub>

      <div className="mt-12 relative">
        {/* vertical rail */}
        <div className="absolute left-[26px] top-4 bottom-4 w-px hidden lg:block"
          style={{ background: `linear-gradient(180deg, ${P.silver}66 0%, ${P.blue}66 50%, ${P.green}88 100%)` }} aria-hidden="true" />
        <ol className="space-y-4">
          {t.platform.stack.map((s, i) => {
            const Ic = s.icon;
            const c = i === 4 ? P.green : (i === 3 ? P.blue : P.silver);
            return (
              <li key={i} className="flex gap-5" data-testid={`stack-${i}`}>
                <div className="shrink-0">
                  <div className="w-[54px] h-[54px] rounded-full flex items-center justify-center relative z-10"
                    style={{ background: "rgba(8,11,18,0.94)", border: `1.5px solid ${c}88` }}>
                    <Ic size={20} style={{ color: c }} strokeWidth={1.75} />
                  </div>
                </div>
                <div className="flex-1 rounded-md p-4 lg:p-5"
                  style={{ background: `linear-gradient(90deg, ${c}0a 0%, ${P.surface} 60%)`, border: `1px solid ${c}33` }}>
                  <div className="text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: c }}>Step {i + 1}</div>
                  <div className="mt-1 text-[15px] font-semibold tracking-tight" style={{ color: "#fff" }}>{s.title}</div>
                  <div className="mt-1 text-[12.5px] leading-relaxed" style={{ color: P.dim }}>{s.desc}</div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mt-10">
        <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: P.silver }}>{t.platform.capsTitle}</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {t.platform.caps.map((cap, i) => (
            <span key={i} className="text-[11px] uppercase tracking-[0.22em] px-2.5 py-1 rounded"
              style={{ background: "rgba(255,255,255,0.04)", color: P.silver, border: `1px solid ${P.border}` }}>
              {cap}
            </span>
          ))}
        </div>
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Partners

const Partners = () => {
  const t = useT();
  return (
    <Section id="partners">
      <Eyebrow color={P.purple}>{t.partners.eyebrow}</Eyebrow>
      <H2>{t.partners.title}</H2>
      <Sub>{t.partners.subtitle}</Sub>

      <div className="mt-10 space-y-6">
        {t.partners.groups.map((g, gi) => (
          <div key={gi} data-testid={`partner-group-${gi}`}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>{g.label}</div>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {g.ids.map((id) => {
                const s = partnerBySlug(id);
                if (!s) return null;
                const isProposed = (s.status || "").toLowerCase().includes("proposed");
                return (
                  <Link key={id} to={`/partners/${id}`}
                    data-testid={`partner-card-${id}`}
                    className="rounded-lg p-4 flex items-center gap-3 transition hover:-translate-y-0.5"
                    style={{ background: P.surface, border: `1px solid ${P.border}` }}>
                    {s.logo ? (
                      <div className="w-11 h-11 rounded flex items-center justify-center shrink-0"
                        style={{ background: "rgba(255,255,255,0.94)" }}>
                        <img src={s.logo} alt={`${s.name} logo`} className="max-h-7 max-w-9 object-contain" />
                      </div>
                    ) : (
                      <div className="w-11 h-11 rounded flex items-center justify-center shrink-0 font-bold text-[15px]"
                        style={{ background: `${P.blue}22`, color: P.blue }}>{s.name[0]}</div>
                    )}
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold truncate" style={{ color: "#fff" }}>{s.name}</div>
                      <div className="text-[10.5px] truncate" style={{ color: isProposed ? P.amber : P.dim }}>
                        {isProposed ? t.partners.proposed : (s.country || s.category)}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Link to="/partners" data-testid="partners-directory-cta"
          className="inline-flex items-center gap-2 text-[12px] font-semibold" style={{ color: P.purple }}>
          {t.partners.cta} <ArrowUpRight size={12} />
        </Link>
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Roadmap

const YEAR_ICONS = [Rocket, HeartPulse, Factory, Building2, Sparkles];

const Roadmap = () => {
  const t = useT();
  return (
    <Section id="roadmap">
      <Eyebrow color={P.cyan}>{t.roadmap.eyebrow}</Eyebrow>
      <H2>{t.roadmap.title}</H2>
      <Sub>{t.roadmap.subtitle}</Sub>

      <div className="mt-10 relative">
        <div className="absolute left-[46px] top-4 bottom-4 w-px hidden lg:block"
          style={{ background: `linear-gradient(180deg, ${P.green}88 0%, ${P.amber}88 25%, ${P.blue}88 50%, ${P.purple}88 75%, ${P.cyan}88 100%)` }} aria-hidden="true" />
        <ol className="space-y-4">
          {t.roadmap.years.map((y, i) => {
            const c = COLOR[y.color] || P.silver;
            const Ic = YEAR_ICONS[i] || CheckCircle2;
            return (
              <li key={i} className="flex gap-5" data-testid={`roadmap-${y.year.replace("+", "-plus")}`}>
                <div className="shrink-0 flex items-center gap-3">
                  <div className="hidden lg:block text-[11px] uppercase tracking-[0.24em] font-semibold w-[38px] text-right" style={{ color: c }}>{y.year}</div>
                  <div className="w-[54px] h-[54px] rounded-full flex items-center justify-center relative z-10"
                    style={{ background: "rgba(8,11,18,0.94)", border: `1.5px solid ${c}88`, boxShadow: i === 0 ? `0 0 0 4px ${c}22, 0 0 22px ${c}66` : "none" }}>
                    <Ic size={20} style={{ color: c }} strokeWidth={1.75} />
                  </div>
                </div>
                <div className="flex-1 rounded-md p-4 lg:p-5"
                  style={{ background: `linear-gradient(90deg, ${c}0a 0%, ${P.surface} 60%)`, border: `1px solid ${c}33` }}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: c }}>
                        {y.year} · {y.status}
                      </div>
                      <div className="mt-1 text-[15px] font-semibold tracking-tight" style={{ color: "#fff" }}>{y.title}</div>
                      <div className="mt-1 text-[12.5px] leading-relaxed max-w-2xl" style={{ color: P.dim }}>{y.desc}</div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </Section>
  );
};

// -------------------------------------------------------- Market Initiatives

const MarketInitiatives = () => (
  <section id="market-initiatives" className="border-t" style={{ borderColor: P.border, background: P.bg }} data-testid="market-initiatives">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#C9A24A" }}>Market Initiatives</div>
      <h2 className="mt-3 font-semibold tracking-tight text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.05]"
          style={{ color: "#fff", letterSpacing: "-0.02em" }}>
        Strategic markets currently under exploration.
      </h2>
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link to="/cabo-verde" data-testid="mi-card-cabo-verde"
          className="rounded-xl p-6 flex flex-col transition hover:brightness-110"
          style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid #C9A24A88` }}>
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#C9A24A" }}>
            Strategic Initiative · 2026
          </div>
          <div className="mt-3 text-[26px] font-semibold tracking-tight" style={{ color: "#fff" }}>Cabo Verde</div>
          <div className="mt-1 text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.silver }}>
            Digital Solutions & Pilot Development
          </div>
          <p className="mt-4 text-[13px] leading-relaxed flex-1" style={{ color: P.dim }}>
            Exploring locally validated opportunities across digital tourism, mobility and integrated technology solutions.
          </p>
          <div className="mt-5 inline-flex items-center gap-1.5 self-start text-[11.5px] font-semibold uppercase tracking-[0.2em]"
               style={{ color: "#C9A24A" }}>
            Explore BSGOTECH × Cabo Verde <ArrowUpRight size={12} />
          </div>
        </Link>
      </div>
    </div>
  </section>
);



// -------------------------------------------------------- Footer

const Footer = () => {
  const t = useT();
  return (
    <footer className="border-t" style={{ borderColor: P.border, background: P.bg }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/bsgotech/logo-official.png" alt="BSGOTECH" className="h-9 w-auto" data-testid="footer-bsgotech-logo" />
          <div className="text-[11px]" style={{ color: P.silver }}>{t.footer.line1}</div>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/reports" data-testid="footer-reports-link"
            className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>
            Executive Reports <ArrowUpRight size={10} />
          </Link>
          <Link to="/ops" data-testid="footer-ops-link"
            className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.24em] opacity-60 hover:opacity-100 transition"
            style={{ color: P.silver }} title="Internal Operations Center">
            · Ops
          </Link>
          <div className="text-[10.5px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>{t.footer.line2}</div>
        </div>
      </div>
    </footer>
  );
};

// -------------------------------------------------------- Page

export default function BsgotechHome() {
  React.useEffect(() => {
    document.title = "BSGOTECH · Engineering Intelligent Ecosystems";
  }, []);
  return (
    <div className="relative" style={{ background: P.bg, color: P.ink }} data-testid="page-bsgotech-home">
      <LanguageSwitcher />
      <Hero />
      <GlobalSearch />
      <EcosystemArchitecture />
      <BrandCards />
      <About />
      <Arch />
      <TechQuiz />
      <Divisions />
      <Platform />
      <Partners />
      <Roadmap />
      <InnovationPathways />
      <MarketInitiatives />
      <Footer />
    </div>
  );
}
